import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import Layout from '../layout/layout';
import {api}from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from "../components/ToastContext";
import { evaluateAndReturn } from '../Utils/common';

// Custom hook to consolidate discount data state management
const useDiscountData = () => {
  const [discountDetails, setDiscountDetails] = useState([]);
  const [cloudProviders, setCloudProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem('userId');
      const [discounts, providers, clientList] = await Promise.all([
        api.get(`/Project/discountDetails/${userId}`).then(r => r.data),
        api.get('/Client/cloudProviders').then(r => r.data),
        api.get(`/Client/GetOrganizationDetailsByUserId/${userId}`).then(r => r.data),
      ]);
      setDiscountDetails(discounts);
      setCloudProviders(providers);
      setClients(clientList);
    } catch (error) {
      console.error('Error fetching discount data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  return { discountDetails, setDiscountDetails, cloudProviders, clients, loading };
};

const Discount = () => {
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  
  // Consolidated data state
  const { discountDetails, cloudProviders, clients, loading, fetchAllData } = useDiscountData();
  
  // Consolidated dialog and selection state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper: Fetch discount by ID from API
  const fetchDiscountById = async (discountId) => {
    const response = await api.get(`/Project/discount/${discountId}`, {
      headers: {
        'X-CORRELATION-ID': 'e7707845-953b-4baa-8dea-260aa7dcf8f9',
      },
    });
    return response.data;
  };

  // Helper: Transform discount data to form structure
  const transformDiscountData = (discountData) => {
    return {
      ...discountData,
      cloudProvider: discountData.cloudProvider.toString(),
      Organization_Id: discountData.Organization_Id,
    };
  };

  // Helper: Build request payload for saving discount
  const buildDiscountRequestBody = (selectedDiscount) => {
    const { cloudProvider, clientId, discountPercentage } = selectedDiscount;
    const userId = sessionStorage.getItem('userId');
    
    return {
      cloudProvider: cloudProvider.toString(),
      userId,
      clientId,
      discountPercentage: Number.parseFloat(discountPercentage),
    };
  };

  // Helper: Save discount via API
  const sendDiscountUpdate = async (id, requestBody) => {
    return await api.post(`/Project/discount/${id}`, requestBody, {
      headers: {
        'X-CORRELATION-ID': 'e7707845-953b-4baa-8dea-260aa7dcf8f9',
        'Content-Type': 'application/json',
      },
    });
  };

  // Helper: Handle successful save
  const onSaveSuccess = async () => {
    await fetchAllData();
    toast.success('Discount updated successfully.');
    handleDialogClose();
  };

  // Helper: Handle save error
  const onSaveError = (error) => {
    console.error('Error updating discount:', error);
    toast.error(error.message || 'An error occurred while updating the discount.');
  };

  const handleEdit = async (discountId) => {
    try {
      const discountData = await fetchDiscountById(discountId);
      const transformedData = transformDiscountData(discountData);
      setSelectedDiscount(transformedData);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching discount:', error);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedDiscount(null);
  };

  // Helper: Handle response status check
  const handleDiscountResponseStatus = async (response) => {
    if (response.status === 200) {
      await onSaveSuccess();
    } else {
      toast.error('Failed to update discount.');
    }
  };

  // Helper: Execute the discount update with error handling
  const executeDiscountUpdate = async () => {
    try {
      const requestBody = buildDiscountRequestBody(selectedDiscount);
      const response = await sendDiscountUpdate(selectedDiscount.id, requestBody);
      await handleDiscountResponseStatus(response);
    } catch (error) {
      onSaveError(error);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedDiscount) return;
    await executeDiscountUpdate();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = (event) => {
    // Implement delete logic here
  };

  const filteredDiscounts = useMemo(() => {
    if (!searchTerm) return discountDetails;
    const lowerCaseTerm = searchTerm.toLowerCase();
    return discountDetails.filter(discount =>
      discount.client.toLowerCase().includes(lowerCaseTerm) ||
      discount.cloudProvider.toLowerCase().includes(lowerCaseTerm)
    );
  }, [discountDetails, searchTerm]);

  const columns = [
    { field: 'client', headerName: 'Client', flex: 1 },
    { field: 'cloudProvider', headerName: 'Cloud Provider', flex: 1 },
    { field: 'discountPercentage', headerName: 'Discount Percentage', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <>
          <Box
            component="span"
            sx={{
              bgcolor: '#e6f4ea',
              color: '#2e7d32',
              border: '1px solid #2e7d32',
              borderRadius: '4px',
              padding: '2px 8px',
              marginRight: '8px',
              marginLeft: '18px',
              cursor: 'pointer',
            }}
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Box>
          {/* <Box
            component="span"
            sx={{
              bgcolor: '#ffebee',
              color: '#d32f2f',
              border: '1px solid #d32f2f',
              borderRadius: '4px',
              padding: '2px 8px',
              cursor: 'pointer',
            }}
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Box> */}
        </>
      ),
    },
  ];

  return (
    <Layout pageType='sideBar'>
      <Box sx={{ flex: 1, pl: theme.spacing(2), bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box sx={{ display: "flex", justifyContent: 'flex-end', width: '100%', alignItems: "center", mb: theme.spacing(2) }}>
          <Button variant="contained" sx={{ color: theme.palette.common.white, bgcolor: theme.palette.primary.main }} onClick={() => navigate('/AddDiscount')}>
            Add Discount
          </Button>
        </Box>

        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: theme.spacing(2), width: '100%' }}
        />

        <Box sx={{ height: 'auto', width: '100%' }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DataGrid
              rows={filteredDiscounts}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              hideFooterSelectedRowCount 
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-root': {
              bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#ffffff'), // Adjust background color based on theme
            },
                '& .MuiDataGrid-filler': {
      display: 'none',
    },
                bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#fff'), // Use black for dark mode and white for light mode
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#2b61aa'), // Adjust header background color
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold !important',
                  // textTransform: "uppercase",
            
                },
                '& .MuiDataGrid-cell': {
                  color: evaluateAndReturn(theme.palette.mode === 'dark', '#ffffff', '#212330'), // Adjust text color based on theme
                  borderBottomWidth: '1px solid',
                  borderColor:evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#2b61aa'),
            
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#2a2d3c', '#fafafa'), // Adjust even row background color
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#ffffff'), // Adjust odd row background color
                },
                '& .MuiDataGrid-footerContainer': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#2a2d3c', '#f0f0f0'), // Adjust footer background color
                },
              }}
            />
          )}
        </Box>

        {selectedDiscount && (
          <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: theme.palette.background.default }}>Edit Discount</DialogTitle>
            <DialogContent sx={{ bgcolor: theme.palette.background.default }}>
                            <FormControl fullWidth margin="dense">
                <InputLabel>Cloud Provider</InputLabel>
                <Select
                  value={selectedDiscount.cloudProvider}
                  onChange={(e) => setSelectedDiscount({ ...selectedDiscount, cloudProvider: e.target.value })}
                >
                  {cloudProviders.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>
                      {provider.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="dense">
                <InputLabel>Client</InputLabel>
                <Select
                  value={selectedDiscount.clientId}
                  onChange={(e) => setSelectedDiscount({ ...selectedDiscount, clientId: e.target.value })}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.clientId} value={client.clientId}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                margin="dense"
                label="Discount Percentage"
                type="number"
                fullWidth
                value={selectedDiscount.discountPercentage}
                onChange={(e) => setSelectedDiscount({ ...selectedDiscount, discountPercentage: e.target.value })}
              />
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.background.default }}>
              <Button onClick={handleDialogClose} variant="cancelContained"
              //  color="primary"
               >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} variant="greenContained" 
              // color="secondary"
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Layout>
  );
};

export default Discount;


